import React from 'react';
import { ActionIcon, CloseButton, Flex, Group, Text, TextInput } from '@mantine/core';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CountBadge } from 'src/UI/page-nav/sidebar/CountBadge';
import { ClickableSectionTitle } from 'src/UI/page-nav/ClickableSectionTitle';

interface PageHeaderProps {
  title: string;
  badgeCount?: number;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchResultCount?: number;
  searchPlaceholder?: string;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

export function PageHeader({
  title,
  badgeCount,
  showSearch = false,
  searchValue = '',
  onSearchChange,
  searchResultCount,
  searchPlaceholder = 'Search',
  actions,
}: PageHeaderProps) {
  const cq = useContainerQuery();
  const isSearching = showSearch && searchValue.trim().length > 0;
  const clearSearch = () => onSearchChange?.('');
  const isStacked = cq({ base: true, sm: false });
  const resultLabel = cq({ base: 'search results', sm: 'results', md: 'search results' });

  return (
    <Flex
      direction={isStacked ? 'column' : 'row'}
      gap="sm"
      align={isStacked ? 'stretch' : 'center'}
      justify="space-between"
    >
      <Group gap="sm" wrap="nowrap">
        {isSearching && (
          <ActionIcon variant="subtle" color="gray.8" onClick={clearSearch} size="lg">
            <IconArrowLeft size={22} />
          </ActionIcon>
        )}
        {isSearching ? (
          <Text
            fz={cq({ base: 'displaySm', xl: 'displayMd' })}
            lh={cq({ base: 'displaySm', xl: 'displayMd' })}
            fw={FONT_WEIGHTS.medium}
            c="gray.10"
            style={{ whiteSpace: 'nowrap' }}
          >
            {`${searchResultCount ?? 0} ${(searchResultCount ?? 0) === 1 ? 'result' : resultLabel}`}
          </Text>
        ) : (
          <ClickableSectionTitle
            fz={cq({ base: 'displaySm', xl: 'displayMd' })}
            lh={cq({ base: 'displaySm', xl: 'displayMd' })}
            fw={FONT_WEIGHTS.medium}
            c="gray.10"
            truncate
            style={{ flex: 1, minWidth: 0 }}
          >
            {title}
          </ClickableSectionTitle>
        )}
        {!isSearching && badgeCount !== undefined && badgeCount > 0 && (
          <CountBadge count={badgeCount} size="large" />
        )}
      </Group>

      <Group
        gap="sm"
        miw={0}
        wrap="nowrap"
        style={{
          flex: isStacked ? 1 : undefined,
          marginLeft: isStacked ? undefined : 'auto',
        }}
      >
        {showSearch && (
          <TextInput
            placeholder={searchPlaceholder}
            leftSection={<IconSearch size={16} />}
            rightSection={searchValue ? <CloseButton size="sm" onClick={clearSearch} /> : undefined}
            value={searchValue}
            onChange={e => onSearchChange?.(e.target.value)}
            variant="compact"
            styles={{ input: { height: 40 } }}
            style={{ flex: isStacked ? 1 : undefined, minWidth: 0 }}
            w={isStacked ? undefined : cq({ sm: 260, xl: 320 })}
          />
        )}
        {actions}
      </Group>
    </Flex>
  );
}
